// Hechos compartidos por varias páginas (guía, sección 1). Una sola fuente para el
// equipo, los modelos, los productos, la prensa y el paper: si un dato cambia, cambia
// en todo el sitio a la vez (texto visible y datos estructurados).

import type { StatusKind } from "@/components/ui/Status";

export type Person = { name: string; role: string; bio: string; slug: string };

// Orden fijo (guía 8.8). Sin edades, colegio ni historia personal (guía 1).
export const team: Person[] = [
  {
    slug: "elias-paniagua-velilla",
    name: "Elias Paniagua Velilla",
    role: "CEO",
    bio: "Dirige la estrategia y las alianzas. Su trabajo es que lo que construimos llegue a quien lo necesita.",
  },
  {
    slug: "juan-manuel-acosta-ramirez",
    name: "Juan Manuel Acosta Ramírez",
    role: "CTO y fundador",
    bio: "Diseñó la arquitectura del modelo. Dirige la investigación y la ingeniería.",
  },
  {
    slug: "mauricio-jose-irrazabal-ruiz-diaz",
    name: "Mauricio José Irrazábal Ruiz Díaz",
    role: "CMO y cofundador",
    bio: "Dirige la marca y la comunicación. Coautor del paper.",
  },
  {
    slug: "jose-rafael-estigarribia-delgado",
    name: "José Rafael Estigarribia Delgado",
    role: "CISO",
    bio: "Responsable de la seguridad y de la protección de los datos de cada cliente.",
  },
];

export type ModelEntry = {
  id: "nano" | "150m" | "1b";
  name: string;
  status: { kind: StatusKind; label: string };
  /** Línea en monoespaciada del bloque 9 del inicio. */
  line: string;
};

export const models: ModelEntry[] = [
  { id: "nano", name: "YvyrAI Nano", status: { kind: "available", label: "Disponible" }, line: "YvyrAI Nano · disponible" },
  { id: "150m", name: "YvyrAI 150M", status: { kind: "progress", label: "En preparación" }, line: "YvyrAI 150M · en preparación" },
  { id: "1b", name: "YvyrAI 1B", status: { kind: "roadmap", label: "En desarrollo" }, line: "YvyrAI 1B · en desarrollo" },
];

export const products = {
  yvyra: { name: "Yvyra", status: { kind: "pilot" as StatusKind, label: "Primeros pilotos" } },
  tajy: { name: "Tajy", status: { kind: "progress" as StatusKind, label: "En desarrollo" } },
};

export type PressItem = { date: string; isoDate: string; outlet: string; label: string; title: string; url: string };

// Cobertura verificada (guía 1), de la más reciente a la más vieja. Sin citas.
export const press: PressItem[] = [
  {
    date: "27 sep 2026",
    isoDate: "2026-09-27",
    outlet: "Tigo Sports",
    label: "Tigo Sports",
    title: "Entrevista en televisión en el programa 100% Franco (desde el minuto 42:52)",
    url: "https://www.youtube.com/watch?v=mO63JM_K7N8&t=2572s",
  },
  {
    date: "13 sep 2026",
    isoDate: "2026-09-13",
    outlet: "La Nación, Gran Diario Domingo",
    label: "La Nación, edición impresa",
    title: "Estudiantes paraguayos desafían a la IA con un modelo creado desde cero",
    url: "https://www.lanacion.com.py/gran-diario-domingo/2026/09/13/estudiantes-paraguayos-desafian-a-la-ia-con-un-modelo-creado-desde-cero/",
  },
  {
    date: "7 sep 2026",
    isoDate: "2026-09-07",
    outlet: "La Nación",
    label: "La Nación",
    title: "Cuatro estudiantes paraguayos desafían a la IA con un modelo creado desde cero",
    url: "https://www.lanacion.com.py/pais/2026/09/07/cuatro-estudiantes-paraguayos-desafian-a-la-ia-con-un-modelo-creado-desde-cero/",
  },
];

export const paperFacts = {
  title:
    "YvyrAI: A Spanish-First Recurrent-Deliberation Language Model Architecture with Internal Verification, Conditional Repair, and Adaptive Compute",
  authors: ["Juan Manuel Acosta Ramírez", "Mauricio José Irrazábal Ruiz Díaz"],
  version: "2.0",
  date: "17 de agosto de 2026",
  isoDate: "2026-08-17",
  publisher: "Zenodo",
  license: "CC BY 4.0",
  conceptDoi: "10.5281/zenodo.20533759",
  conceptDoiUrl: "https://doi.org/10.5281/zenodo.20533759",
  versions: [
    { version: "2.0", date: "17 de agosto de 2026", doi: "10.5281/zenodo.21985693" },
    // 🔵 La guía no da la fecha de la v1.0: se muestra sin fecha hasta confirmarla.
    { version: "1.0", date: null as string | null, doi: "10.5281/zenodo.20533760" },
  ],
  pdfPath: "/YvyrAI-Paper.pdf",
};

// Números de la auditoría (guía 1). Separador de miles con punto.
export const audit = {
  regions: [
    { id: "latam", value: "8.644", count: 8644, label: "Latinoamérica hispana y lenguas indígenas" },
    { id: "brasil", value: "15.804", count: 15804, label: "Brasil y portugués" },
    { id: "espana", value: "1.294", count: 1294, label: "España" },
  ],
  total: "≈ 25.700",
  recheck: { date: "1 de octubre de 2026", models: "10.808" },
};
